/**
 * The one place this app talks to the API.
 *
 * Responsibilities, in the order they matter:
 *
 * 1. **Authorization** - attach the bearer access token from `session`.
 * 2. **Refresh** - a 15-minute access token expires mid-session, so a 401
 *    triggers ONE refresh attempt (single-flight, shared across concurrent
 *    requests) and then ONE retry of the original request. A refresh that fails
 *    ends the session and tells the shell, which sends the admin to the login
 *    page. Retrying more than once is how a client turns a dead session into an
 *    infinite loop against a dead API.
 * 3. **Timeouts** - an `AbortController` per request, because a VPS that accepts
 *    the connection and then hangs would otherwise leave a spinner forever.
 * 4. **Errors** - every failure becomes an `ApiError` (the server's own
 *    sentence) or a `NetworkError`. Nothing here ever returns a raw `Response`
 *    for a caller to interpret.
 */

import { API_BASE_URL, REQUEST_TIMEOUT_MS } from "./config";
import { ApiError, NetworkError, toApiError } from "./errors";
import { session, type AdminIdentity } from "../session";

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /**
   * JSON body, serialized here so no caller has to remember the header - or a
   * `FormData` for the two logo uploads, which is passed through untouched so the
   * browser can set its own multipart `Content-Type`.
   */
  body?: unknown;
  /** Query parameters. `null` / `undefined` / empty-string values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Overrides the default timeout. Exports and reports get more room. */
  timeoutMs?: number;
  /** Set to skip the Authorization header - only the auth routes need this. */
  anonymous?: boolean;
};

export type ApiMeta = {
  /** The HTTP status, for the two callers that branch on 200-vs-201. */
  status: number;
};

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === null || value === undefined || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/** Told when a refresh definitively fails, so the shell can sign the admin out. */
let onSessionLost: (() => void) | null = null;

export function configureSessionLossHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

/**
 * The in-flight refresh, shared by every request that needs one.
 *
 * Without this, a dashboard making five calls with an expired token would fire
 * five refreshes; `rotate_session` is SINGLE-USE, so four of them would fail and
 * revoke the session the first one just created. Single-flight is not an
 * optimization here, it is a correctness requirement.
 */
let refreshInFlight: Promise<AdminIdentity | null> | null = null;

type RefreshBody = {
  access_token?: string;
  refresh_token?: string;
  admin?: AdminIdentity;
};

async function attemptRefresh(refreshToken: string): Promise<AdminIdentity | null> {
  let response: Response;
  try {
    response = await fetch(buildUrl("/admin/auth/refresh"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
      // No Authorization header: the refresh token IS the credential, and the
      // access token is the thing that just expired.
    });
  } catch {
    // The API is unreachable, which is NOT a reason to discard a refresh token
    // that may still be good once the network returns.
    return null;
  }

  if (!response.ok) {
    // `invalid_refresh_token` (401) means the token is spent, revoked, or
    // expired - the session is over, so the stored copy is cleared. Any other
    // failure is left alone for the same reason as above.
    if (response.status === 401) {
      session.clear();
      onSessionLost?.();
    }
    return null;
  }

  const body = (await response.json()) as RefreshBody;
  if (!body.access_token || !body.refresh_token) {
    session.clear();
    onSessionLost?.();
    return null;
  }

  session.rotate(body.access_token, body.refresh_token);
  if (body.admin) session.setIdentity(body.admin);

  const identity = body.admin ?? session.getIdentity();
  if (!identity) {
    // Should not happen: refresh does not change the account. Treated as a lost
    // session rather than as a valid one with no name to show.
    session.clear();
    onSessionLost?.();
    return null;
  }
  return identity;
}

function refreshOnce(): Promise<AdminIdentity | null> {
  const refreshToken = session.getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);

  refreshInFlight ??= attemptRefresh(refreshToken).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * Re-establish the session after a page reload.
 *
 * The access token is held in memory only, so a reload leaves the app with a
 * refresh token and nothing to send. This is the one place the app refreshes
 * WITHOUT a 401 prompting it, and it is what makes the "no persisted access
 * token" posture workable: the reload costs one request, not a re-login.
 *
 * Shares `refreshInFlight` with the 401 path, so a reload that races the first
 * screen's requests still performs exactly one rotation - which matters because
 * `rotate_session` is single-use.
 */
export function restoreSession(): Promise<AdminIdentity | null> {
  return refreshOnce();
}

async function send(
  path: string,
  options: RequestOptions,
  accessToken: string | null,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    options.timeoutMs ?? REQUEST_TIMEOUT_MS,
  );

  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (options.body instanceof FormData) {
    // The logo uploads (`POST /admin/{vendors,family-biz}/{id}/logo`). The body
    // is passed through untouched and NO Content-Type is set - the browser has to
    // generate it itself, because it is the only thing that knows the multipart
    // boundary. Setting it by hand here produces a request the API parses as
    // empty, which reads as "No logo was attached." rather than as a bug here.
    body = options.body;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  if (accessToken && !options.anonymous) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  try {
    return await fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers,
      body,
      signal: controller.signal,
    });
  } catch {
    if (controller.signal.aborted) {
      throw new NetworkError(
        "The server took too long to answer. Check your connection and try again.",
      );
    }
    throw new NetworkError(
      `Could not reach the API at ${API_BASE_URL}. Check that it is running and try again.`,
    );
  } finally {
    window.clearTimeout(timeout);
  }
}

function ensureJson<T>(response: Response): Promise<T> {
  if (response.status === 204) return Promise.resolve(undefined as T);
  return response.json() as Promise<T>;
}

/**
 * Make an API call and return its parsed JSON body.
 *
 * Throws `ApiError` for any non-2xx (carrying the server's `message` / `action`)
 * and `NetworkError` if the request could not be completed.
 */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
  meta?: ApiMeta,
): Promise<T> {
  let response = await send(path, options, session.getAccessToken());

  if (response.status === 401 && !options.anonymous && session.getRefreshToken()) {
    const identity = await refreshOnce();
    if (identity) {
      // Exactly one retry, with the token the refresh just issued. If THIS 401s,
      // the session is genuinely over and the error is rendered as such.
      response = await send(path, options, session.getAccessToken());
    }
  }

  if (!response.ok) {
    const error = await toApiError(response);
    // A 401 or 403 `forbidden` on an admin route means the stored session can no
    // longer do this work, so the shell should stop showing signed-in chrome.
    if (error.status === 401 || error.code === "forbidden") {
      if (session.getAccessToken()) {
        session.clear();
        onSessionLost?.();
      }
    }
    throw error;
  }

  if (meta) meta.status = response.status;
  return ensureJson<T>(response);
}

/**
 * Download a file (the referrals CSV, `GET /admin/referrals/export.csv`).
 *
 * Fetched as a blob rather than navigated to, because a plain `window.open` cannot
 * send the `Authorization` header - the endpoint is admin-only and a bare link
 * would therefore 401. The filename comes from the server's
 * `Content-Disposition`, so the two stay in step.
 */
export async function downloadFile(
  path: string,
  options: RequestOptions = {},
): Promise<void> {
  let response = await send(path, options, session.getAccessToken());

  if (response.status === 401 && session.getRefreshToken()) {
    const identity = await refreshOnce();
    if (identity) response = await send(path, options, session.getAccessToken());
  }

  if (!response.ok) throw await toApiError(response);

  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ?? "download.csv";

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    // Appended for the click to register in Firefox, then removed; the object URL
    // is revoked so a large export is not held for the tab's lifetime.
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export { ApiError, NetworkError };
